/**
 * data/instrumentCatalog/stocks.ts
 * Stocks, ETFs, and ADRs reference catalog
 * 
 * Contains actively traded equities organized by market segment
 * with proper sub-asset class taxonomy (stock/etf/adr).
 * 
 * Research file: docs/reference/InstrumentCatalogResearch/Stocks_DataPopulationResearch.md
 */

import type { InstrumentCatalogEntry } from '@/types/reference/InstrumentCatalog';

/**
 * Stocks instrument catalog
 * 
 * Contains 72 equity instruments:
 * - 30 individual US stocks (mega/large-cap)
 * - 25 ETFs (broad market, sector, regional)
 * - 17 ADRs (EU and emerging market exposure)
 */
export const STOCKS_INSTRUMENTS: InstrumentCatalogEntry[] = [
  
  // ===== STOCKS & ETFS =====
  
  // US Large-Cap Stocks
  // US Large-Cap Stocks
  {
    id: 'AAPL_STOCKS',
    symbol: 'AAPL',
    name: 'Apple Inc. TEST',
    shortName: 'Apple',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Mega-cap tech'
  },
  
  {
    id: 'MSFT_STOCKS',
    symbol: 'MSFT',
    name: 'Microsoft Corp.',
    shortName: 'Microsoft',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Mega-cap tech'
  },
  
  {
    id: 'NVDA_STOCKS',
    symbol: 'NVDA',
    name: 'NVIDIA Corp.',
    shortName: 'NVIDIA',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'contract',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'AI & semis leader'
  },
  
  {
    id: 'AMZN_STOCKS',
    symbol: 'AMZN',
    name: 'Amazon.com Inc.',
    shortName: 'Amazon',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'E-commerce & cloud'
  },
  
  {
    id: 'GOOGL_STOCKS',
    symbol: 'GOOGL',
    name: 'Alphabet Inc. Class A',
    shortName: 'Alphabet A',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Google parent (Class A)'
  },
  
  {
    id: 'GOOG_STOCKS',
    symbol: 'GOOG',
    name: 'Alphabet Inc. Class C',
    shortName: 'Alphabet C',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Google parent (Class C)'
  },
  
  {
    id: 'META_STOCKS',
    symbol: 'META',
    name: 'Meta Platforms Inc.',
    shortName: 'Meta',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Social media & VR'
  },
  
  {
    id: 'TSLA_STOCKS',
    symbol: 'TSLA',
    name: 'Tesla Inc.',
    shortName: 'Tesla',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'EV & energy'
  },
  
  {
    id: 'BRK.B',
    symbol: 'BRK.B',
    name: 'Berkshire Hathaway Inc. Class B',
    shortName: 'Berkshire',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Diversified conglomerate'
  },
  
  {
    id: 'UNH_STOCKS',
    symbol: 'UNH',
    name: 'UnitedHealth Group Inc.',
    shortName: 'UnitedHealth',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Managed health care'
  },
  
  {
    id: 'JNJ_STOCKS',
    symbol: 'JNJ',
    name: 'Johnson & Johnson',
    shortName: 'J&J',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Pharmaceuticals & medtech'
  },
  
  {
    id: 'LLY_STOCKS',
    symbol: 'LLY',
    name: 'Eli Lilly and Company',
    shortName: 'Lilly',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Pharma (obesity/diabetes)'
  },
  
  {
    id: 'JPM_STOCKS',
    symbol: 'JPM',
    name: 'JPMorgan Chase & Co.',
    shortName: 'JPMorgan',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Largest US bank'
  },
  
  {
    id: 'V_STOCKS',
    symbol: 'V',
    name: 'Visa Inc.',
    shortName: 'Visa',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Payments network'
  },
  
  {
    id: 'MA_STOCKS',
    symbol: 'MA',
    name: 'Mastercard Inc.',
    shortName: 'Mastercard',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Payments network'
  },
  
  {
    id: 'WMT_STOCKS',
    symbol: 'WMT',
    name: 'Walmart Inc.',
    shortName: 'Walmart',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Retail giant'
  },
  
  {
    id: 'PG_STOCKS',
    symbol: 'PG',
    name: 'Procter & Gamble Co.',
    shortName: 'P&G',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Consumer staples'
  },
  
  {
    id: 'XOM_STOCKS',
    symbol: 'XOM',
    name: 'Exxon Mobil Corp.',
    shortName: 'Exxon',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Integrated energy'
  },
  
  {
    id: 'AVGO_STOCKS',
    symbol: 'AVGO',
    name: 'Broadcom Inc.',
    shortName: 'Broadcom',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Semis & infrastructure software'
  },
  
  {
    id: 'HD_STOCKS',
    symbol: 'HD',
    name: 'Home Depot Inc.',
    shortName: 'Home Depot',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Home improvement retail'
  },
  
  {
    id: 'PEP_STOCKS',
    symbol: 'PEP',
    name: 'PepsiCo Inc.',
    shortName: 'PepsiCo',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Beverages & snacks'
  },
  
  {
    id: 'KO_STOCKS',
    symbol: 'KO',
    name: 'The Coca-Cola Company',
    shortName: 'Coca-Cola',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Beverages'
  },
  
  {
    id: 'ORCL_STOCKS',
    symbol: 'ORCL',
    name: 'Oracle Corp.',
    shortName: 'Oracle',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Enterprise software & cloud'
  },
  
  {
    id: 'COST_STOCKS',
    symbol: 'COST',
    name: 'Costco Wholesale Corp.',
    shortName: 'Costco',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Warehouse retail'
  },
  
  {
    id: 'CRM_STOCKS',
    symbol: 'CRM',
    name: 'Salesforce Inc.',
    shortName: 'Salesforce',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Enterprise SaaS'
  },
  
  {
    id: 'MRK_STOCKS',
    symbol: 'MRK',
    name: 'Merck & Co., Inc.',
    shortName: 'Merck',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Pharmaceuticals'
  },
  
  {
    id: 'ADBE_STOCKS',
    symbol: 'ADBE',
    name: 'Adobe Inc.',
    shortName: 'Adobe',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Creative & marketing software'
  },
  
  {
    id: 'CSCO_STOCKS',
    symbol: 'CSCO',
    name: 'Cisco Systems Inc.',
    shortName: 'Cisco',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Networking hardware & software'
  },
  
  {
    id: 'AMD_STOCKS',
    symbol: 'AMD',
    name: 'Advanced Micro Devices Inc.',
    shortName: 'AMD',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Semiconductors'
  },
  
  {
    id: 'NFLX_STOCKS',
    symbol: 'NFLX',
    name: 'Netflix Inc.',
    shortName: 'Netflix',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'stock'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Streaming media'
  },
  
  // US ETFs - Broad Market
  {
    id: 'SPY_STOCKS',
    symbol: 'SPY',
    name: 'SPDR S&P 500 ETF Trust',
    shortName: 'SPY',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'US large-cap broad market'
  },
  
  {
    id: 'VOO_STOCKS',
    symbol: 'VOO',
    name: 'Vanguard S&P 500 ETF',
    shortName: 'VOO',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'US large-cap broad market'
  },
  
  {
    id: 'IVV_STOCKS',
    symbol: 'IVV',
    name: 'iShares Core S&P 500 ETF',
    shortName: 'IVV',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'US large-cap broad market'
  },
  
  {
    id: 'VTI_STOCKS',
    symbol: 'VTI',
    name: 'Vanguard Total Stock Market ETF',
    shortName: 'VTI',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Total US market'
  },
  
  {
    id: 'QQQ_STOCKS',
    symbol: 'QQQ',
    name: 'Invesco QQQ Trust',
    shortName: 'QQQ',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Nasdaq-100 (tech-heavy)'
  },
  
  {
    id: 'DIA_STOCKS',
    symbol: 'DIA',
    name: 'SPDR Dow Jones Industrial Average ETF',
    shortName: 'DIA',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Dow 30'
  },
  
  {
    id: 'IWM_STOCKS',
    symbol: 'IWM',
    name: 'iShares Russell 2000 ETF',
    shortName: 'IWM',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'US small caps'
  },
  
  {
    id: 'VT_STOCKS',
    symbol: 'VT',
    name: 'Vanguard Total World Stock ETF',
    shortName: 'VT',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Global stocks (ex/US included)'
  },
  
  // US Sector ETFs
  {
    id: 'XLK_STOCKS',
    symbol: 'XLK',
    name: 'Technology Select Sector SPDR Fund',
    shortName: 'XLK',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'US tech sector'
  },
  
  {
    id: 'XLF_STOCKS',
    symbol: 'XLF',
    name: 'Financial Select Sector SPDR Fund',
    shortName: 'XLF',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'US financials sector'
  },
  
  {
    id: 'XLV_STOCKS',
    symbol: 'XLV',
    name: 'Health Care Select Sector SPDR Fund',
    shortName: 'XLV',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'US healthcare sector'
  },
  
  {
    id: 'XLE_STOCKS',
    symbol: 'XLE',
    name: 'Energy Select Sector SPDR Fund',
    shortName: 'XLE',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'US energy sector'
  },
  
  {
    id: 'XLY_STOCKS',
    symbol: 'XLY',
    name: 'Consumer Discretionary Select Sector SPDR Fund',
    shortName: 'XLY',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'US consumer discretionary'
  },
  
  {
    id: 'XLP_STOCKS',
    symbol: 'XLP',
    name: 'Consumer Staples Select Sector SPDR Fund',
    shortName: 'XLP',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'US consumer staples'
  },
  
  {
    id: 'XLI_STOCKS',
    symbol: 'XLI',
    name: 'Industrial Select Sector SPDR Fund',
    shortName: 'XLI',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'US industrials'
  },
  
  {
    id: 'XLB_STOCKS',
    symbol: 'XLB',
    name: 'Materials Select Sector SPDR Fund',
    shortName: 'XLB',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'US materials'
  },
  
  {
    id: 'XLRE_STOCKS',
    symbol: 'XLRE',
    name: 'Real Estate Select Sector SPDR Fund',
    shortName: 'XLRE',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'US real estate'
  },
  
  {
    id: 'XLU_STOCKS',
    symbol: 'XLU',
    name: 'Utilities Select Sector SPDR Fund',
    shortName: 'XLU',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'US utilities'
  },
  
  {
    id: 'XLC_STOCKS',
    symbol: 'XLC',
    name: 'Communication Services Select Sector SPDR Fund',
    shortName: 'XLC',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'US comm services'
  },
  
  {
    id: 'SMH_STOCKS',
    symbol: 'SMH',
    name: 'VanEck Semiconductor ETF',
    shortName: 'SMH',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'us-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'US/global semiconductors'
  },
  
  // EU ADRs
  {
    id: 'AZN_STOCKS',
    symbol: 'AZN',
    name: 'AstraZeneca PLC ADR',
    shortName: 'AstraZeneca',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'eu-equities',
      subAssetClass: 'adr'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'UK-based pharma ADR'
  },
  
  {
    id: 'NVO_STOCKS',
    symbol: 'NVO',
    name: 'Novo Nordisk A/S ADR',
    shortName: 'Novo Nordisk',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'eu-equities',
      subAssetClass: 'adr'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Denmark-based pharma ADR'
  },
  
  {
    id: 'SAP_STOCKS',
    symbol: 'SAP',
    name: 'SAP SE ADR',
    shortName: 'SAP',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'eu-equities',
      subAssetClass: 'adr'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Germany-based software ADR'
  },
  
  {
    id: 'SHEL_STOCKS',
    symbol: 'SHEL',
    name: 'Shell PLC ADR',
    shortName: 'Shell',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'eu-equities',
      subAssetClass: 'adr'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'UK-based energy ADR'
  },
  
  {
    id: 'BP_STOCKS',
    symbol: 'BP',
    name: 'BP PLC ADR',
    shortName: 'BP',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'eu-equities',
      subAssetClass: 'adr'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'UK-based energy ADR'
  },
  
  {
    id: 'RIO_STOCKS',
    symbol: 'RIO',
    name: 'Rio Tinto PLC ADR',
    shortName: 'Rio Tinto',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'eu-equities',
      subAssetClass: 'adr'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'UK/Australia mining ADR'
  },
  
  {
    id: 'HSBC_STOCKS',
    symbol: 'HSBC',
    name: 'HSBC Holdings PLC ADR',
    shortName: 'HSBC',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'eu-equities',
      subAssetClass: 'adr'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'UK-based bank ADR'
  },
  
  // Emerging Markets ADRs
  {
    id: 'BABA_STOCKS',
    symbol: 'BABA',
    name: 'Alibaba Group Holding ADR',
    shortName: 'Alibaba',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'emerging-markets',
      subAssetClass: 'adr'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'China e-commerce ADR'
  },
  
  {
    id: 'TSM_STOCKS',
    symbol: 'TSM',
    name: 'Taiwan Semiconductor Mfg. ADR',
    shortName: 'TSMC',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'emerging-markets',
      subAssetClass: 'adr'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Taiwan semis ADR'
  },
  
  {
    id: 'NIO_STOCKS',
    symbol: 'NIO',
    name: 'NIO Inc. ADR',
    shortName: 'NIO',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'emerging-markets',
      subAssetClass: 'adr'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'China EV ADR'
  },
  
  {
    id: 'PDD_STOCKS',
    symbol: 'PDD',
    name: 'PDD Holdings ADR',
    shortName: 'PDD',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'emerging-markets',
      subAssetClass: 'adr'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'China e-commerce ADR'
  },
  
  // International ETFs - EU Equities
  {
    id: 'EFA_STOCKS',
    symbol: 'EFA',
    name: 'iShares MSCI EAFE ETF',
    shortName: 'EFA',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'eu-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Developed ex-US (Europe + Japan)'
  },
  
  {
    id: 'VGK_STOCKS',
    symbol: 'VGK',
    name: 'Vanguard FTSE Europe ETF',
    shortName: 'VGK',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'eu-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Europe developed markets'
  },
  
  {
    id: 'EWU_STOCKS',
    symbol: 'EWU',
    name: 'iShares MSCI United Kingdom ETF',
    shortName: 'EWU',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'eu-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'UK equities'
  },
  
  {
    id: 'EWG_STOCKS',
    symbol: 'EWG',
    name: 'iShares MSCI Germany ETF',
    shortName: 'EWG',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'eu-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Germany equities'
  },
  
  {
    id: 'EWJ_STOCKS',
    symbol: 'EWJ',
    name: 'iShares MSCI Japan ETF',
    shortName: 'EWJ',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'eu-equities',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Japan equities (developed ex-US)'
  },
  
  // Emerging Markets ETFs
  {
    id: 'EEM_STOCKS',
    symbol: 'EEM',
    name: 'iShares MSCI Emerging Markets ETF',
    shortName: 'EEM',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'emerging-markets',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Emerging markets'
  },
  
  {
    id: 'VWO_STOCKS',
    symbol: 'VWO',
    name: 'Vanguard FTSE Emerging Markets ETF',
    shortName: 'VWO',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'emerging-markets',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Emerging markets'
  },
  
  {
    id: 'FXI_STOCKS',
    symbol: 'FXI',
    name: 'iShares China Large-Cap ETF',
    shortName: 'FXI',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'emerging-markets',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'China large caps'
  },
  
  {
    id: 'INDA_STOCKS',
    symbol: 'INDA',
    name: 'iShares MSCI India ETF',
    shortName: 'INDA',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'emerging-markets',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'India equities'
  },
  
  {
    id: 'EWZ_STOCKS',
    symbol: 'EWZ',
    name: 'iShares MSCI Brazil ETF',
    shortName: 'EWZ',
    classification: {
      assetClass: 'stocks',
      marketSegment: 'emerging-markets',
      subAssetClass: 'etf'
    },
    contractSize: 1,
    contractAsset: 'share',
    incrementSize: 0.01,
    incrementType: 'cent',
    quoteCurrency: 'USD',
    notes: 'Brazil equities'
  }
];